import {
  contrastRatio,
  ensureContrast,
  mix,
  onColor,
  type Hex,
} from './color.ts'

// Semantic color roles. Components read these and nothing else, which is what
// lets one attribute on <html> reskin the whole site for a mission.

export const roleNames = [
  'bg',
  'surface',
  'raised',
  'ink',
  'ink-muted',
  'border',
  'border-strong',
  'primary',
  'primary-hover',
  'on-primary',
  'accent',
  'on-accent',
  'accent-ink',
  'link',
  'focus',
  'success',
  'warning',
  'error',
  'info',
  'flag-red',
  'flag-blue',
] as const

export type RoleName = (typeof roleNames)[number]
export type Roles = Record<RoleName, Hex>
export type ThemePair = { light: Roles; dark: Roles }
export type MissionPalette = { primary: string; secondary: string; accent: string }

export const roleDescriptions: Record<RoleName, string> = {
  bg: 'Page background.',
  surface: 'Quiet sections, cards at rest and the hero canvas.',
  raised: 'Elevated surfaces: menus, dialogs, inputs.',
  ink: 'Primary text and icons.',
  'ink-muted': 'Secondary text. Meets 4.5:1 on every surface.',
  border: 'Decorative dividers.',
  'border-strong': 'Control boundaries. Meets 3:1 on every surface.',
  primary: 'Primary actions and selected states.',
  'primary-hover': 'Hover and pressed state of primary actions.',
  'on-primary': 'Text and icons on primary.',
  accent: 'Signature accent fills: badges, highlights, the flag stripe.',
  'on-accent': 'Text and icons on accent.',
  'accent-ink': 'Accent used as text. Meets 4.5:1 on every surface.',
  link: 'Inline links. Meets 4.5:1 on every surface.',
  focus: 'Keyboard focus ring. Meets 3:1 on every surface.',
  success: 'Success text and icons.',
  warning: 'Warning text and icons.',
  error: 'Error text and icons.',
  info: 'Informational text and icons.',
  'flag-red': 'Flag stripes.',
  'flag-blue': 'Flag canton.',
}

const status = {
  light: {
    success: '#007f26',
    warning: '#8a5300',
    error: '#c5000a',
    info: '#0066c5',
  },
  dark: {
    success: '#3fd06a',
    warning: '#f5b942',
    error: '#ff6b6b',
    info: '#66aef5',
  },
} as const

/**
 * The default American theme, taken from America.gov's published values and
 * checked against the same contrast rules as every derived theme.
 */
export const americaTheme: ThemePair = {
  light: {
    bg: '#ffffff',
    surface: '#f7f7f7',
    raised: '#ffffff',
    ink: '#000c1f',
    'ink-muted': '#4a5160',
    border: '#e3e4e6',
    'border-strong': '#7a808b',
    primary: '#002664',
    'primary-hover': '#00163b',
    'on-primary': '#ffffff',
    accent: '#d80007',
    'on-accent': '#ffffff',
    'accent-ink': '#c5000a',
    link: '#0058ab',
    focus: '#000c1f',
    ...status.light,
    'flag-red': '#d80007',
    'flag-blue': '#002664',
  },
  dark: {
    bg: '#0b0d12',
    surface: '#0f1217',
    raised: '#14171d',
    ink: '#ffffff',
    'ink-muted': '#aeb3bc',
    border: '#262a33',
    'border-strong': '#7d838f',
    primary: '#0066c5',
    'primary-hover': '#1a75d0',
    'on-primary': '#ffffff',
    accent: '#ff5252',
    'on-accent': '#000c1f',
    'accent-ink': '#ff6b6b',
    link: '#66aef5',
    focus: '#ffffff',
    ...status.dark,
    'flag-red': '#ff5252',
    'flag-blue': '#4c8fe0',
  },
}

/** Lightest tint of `color` that still carries white text at 4.5:1. */
function darkModePrimary(color: string, background: string): Hex {
  let best = ensureContrast(color, '#ffffff', 4.5, 'darken')
  for (let step = 0; step <= 100; step += 2) {
    const candidate = mix(color, '#ffffff', step / 100)
    if (contrastRatio('#ffffff', candidate) < 4.5) break
    best = candidate
    if (contrastRatio(candidate, background) >= 2.4) break
  }
  return best
}

/** An accent fill and the text color that reads on it at 4.5:1. */
function accentPair(accent: string, ink: string): [Hex, Hex] {
  const on = onColor(accent, ink)
  if (contrastRatio(on, accent) >= 4.5) return [mix(accent, accent, 0), on]
  return [ensureContrast(accent, on, 4.5), on]
}

/** Status colors nudged until they read on the theme's most contrasting surface. */
function statusOn(mode: 'light' | 'dark', surface: string) {
  return Object.fromEntries(
    Object.entries(status[mode]).map(([name, color]) => [name, ensureContrast(color, surface, 4.6)]),
  ) as Record<keyof (typeof status)['light'], Hex>
}

/** A hover shade that keeps white text at 4.5:1. */
function hoverShade(primary: string, toward: '#ffffff' | '#000000'): Hex {
  const candidate = mix(primary, toward, toward === '#ffffff' ? 0.12 : 0.25)
  return contrastRatio('#ffffff', candidate) >= 4.5
    ? candidate
    : mix(primary, '#000000', 0.18)
}

/** Builds a verified light and dark role set from an agency palette. */
export function deriveTheme(palette: MissionPalette): ThemePair {
  const { primary, secondary, accent } = palette

  const lightSurface = mix('#f7f7f7', primary, 0.035)
  const lightInk = ensureContrast(mix('#000c1f', secondary, 0.2), lightSurface, 13)
  const lightPrimary = ensureContrast(primary, '#ffffff', 4.5)
  const [lightAccent, lightOnAccent] = accentPair(accent, lightInk)
  const light: Roles = {
    bg: '#ffffff',
    surface: lightSurface,
    raised: '#ffffff',
    ink: lightInk,
    'ink-muted': ensureContrast(mix(lightInk, '#ffffff', 0.32), lightSurface, 4.6),
    border: mix(lightSurface, lightInk, 0.1),
    'border-strong': ensureContrast(mix(lightInk, '#ffffff', 0.5), lightSurface, 3.1),
    primary: lightPrimary,
    'primary-hover': hoverShade(lightPrimary, '#000000'),
    'on-primary': '#ffffff',
    accent: lightAccent,
    'on-accent': lightOnAccent,
    'accent-ink': ensureContrast(accent, lightSurface, 4.6),
    link: ensureContrast(primary, lightSurface, 4.6),
    focus: lightInk,
    ...statusOn('light', lightSurface),
    'flag-red': americaTheme.light['flag-red'],
    'flag-blue': lightPrimary,
  }

  const darkBg = mix('#0b0d12', primary, 0.06)
  const darkSurface = mix('#0f1217', primary, 0.08)
  const darkRaised = mix('#14171d', primary, 0.1)
  const darkPrimary = darkModePrimary(primary, darkBg)
  const [darkAccent, darkOnAccent] = accentPair(
    ensureContrast(accent, darkBg, 3, 'lighten'),
    '#000c1f',
  )
  const dark: Roles = {
    bg: darkBg,
    surface: darkSurface,
    raised: darkRaised,
    ink: '#ffffff',
    'ink-muted': ensureContrast(mix('#ffffff', darkRaised, 0.3), darkRaised, 4.6, 'lighten'),
    border: mix(darkRaised, '#ffffff', 0.1),
    'border-strong': ensureContrast(mix('#ffffff', darkRaised, 0.5), darkRaised, 3.1, 'lighten'),
    primary: darkPrimary,
    'primary-hover': hoverShade(darkPrimary, '#ffffff'),
    'on-primary': '#ffffff',
    accent: darkAccent,
    'on-accent': darkOnAccent,
    'accent-ink': ensureContrast(accent, darkRaised, 4.6, 'lighten'),
    link: ensureContrast(primary, darkRaised, 4.6, 'lighten'),
    focus: '#ffffff',
    ...statusOn('dark', darkRaised),
    'flag-red': americaTheme.dark['flag-red'],
    'flag-blue': ensureContrast(primary, darkBg, 3, 'lighten'),
  }
  return { light, dark }
}

/** Every pairing a component is allowed to make, with its minimum ratio. */
export const contrastPairs: Array<[RoleName, RoleName, number]> = [
  ...(['bg', 'surface', 'raised'] as const).flatMap(
    (surface): Array<[RoleName, RoleName, number]> => [
      ['ink', surface, 7],
      ['ink-muted', surface, 4.5],
      ['link', surface, 4.5],
      ['accent-ink', surface, 4.5],
      ['border-strong', surface, 3],
      ['focus', surface, 3],
      ['success', surface, 4.5],
      ['warning', surface, 4.5],
      ['error', surface, 4.5],
      ['info', surface, 4.5],
    ],
  ),
  ['on-primary', 'primary', 4.5],
  ['on-primary', 'primary-hover', 4.5],
  ['on-accent', 'accent', 4.5],
]

export function themeFailures(theme: ThemePair) {
  return (['light', 'dark'] as const).flatMap((mode) =>
    contrastPairs
      .map(([fg, bg, min]) => ({
        mode,
        fg,
        bg,
        min,
        ratio: contrastRatio(theme[mode][fg], theme[mode][bg]),
      }))
      .filter((pair) => pair.ratio < pair.min),
  )
}

export function roleDeclarations(roles: Roles, indent = '  ') {
  return roleNames.map((name) => `${indent}--mds-${name}: ${roles[name]};`).join('\n')
}
